import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import TaxbillList from '../../components/paymentmng/TaxbillList';
import { TaxbillListBox } from '../../components/paymentmng/TaxbillListBox';
import TaxbillStatusList from '../../components/paymentmng/TaxbillStatusList';
import { listTaxbills } from '../../modules/payModule/taxbills';

const PaymentListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  // 홈 위젯(수금 예정/미수 연령)에서 ?billTyp=/&reqCompYn=/&dateField=/&strtDt=/&endDt=/
  // &salesUsrUuid= 로 들어오면 그 필터로 초기 조회한다 - 카드=목록 건수를 맞추기 위한 최소
  // 딥링크 지원(메인화면 재검토, 2026-10-23). 최초 마운트 값만 읽는다.
  const [searchParams] = useSearchParams();
  const [searchBillTyp, setsearchBillTyp] = useState(searchParams.get('billTyp') || '');
  const [searchTyp, setSearchTyp] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('invNo');
  const [searchWords, setSearchWords] = useState('');
  const [searchYn, setSearchYn] = useState(searchParams.get('reqCompYn') || 'Y');
  const [searchStrtDt, setSearchStrtDt] = useState(searchParams.get('strtDt') || '');
  const [searchEndDt, setSearchEndDt] = useState(searchParams.get('endDt') || '');
  const [searchDateField] = useState(searchParams.get('dateField') || '');
  const [searchSalesUsrUuid] = useState(searchParams.get('salesUsrUuid') || '');

  const { taxbills, error, loading } = useSelector(
    ({ payModule, loading }) => ({
      taxbills: payModule.taxbills.taxbills,
      error: payModule.taxbills.error,
      loading: loading['pay/LIST_TAXBILLS'],
    }),
  );

  const searchList = (values) => {
    const startDate =
      values.searchStrtDt !== null && values.searchStrtDt !== ''
        ? dayjs(values.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      values.searchEndDt !== null && values.searchEndDt !== ''
        ? dayjs(values.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      billTyp: searchBillTyp,
      searchTyp: searchTyp,
      reqCompYn: searchYn,
      strtDt: startDate,
      endDt: endDate,
      // 홈 위젯 딥링크 필터(메인화면 재검토, 2026-10-23) - dateField가 없으면 서버가 기존
      // 레거시 분기(REQ_COMP_DT 기준)를 그대로 쓴다.
      dateField: searchDateField || undefined,
      salesUsrUuid: searchSalesUsrUuid || undefined,
      [values.searchKeyword]: values.searchWords,
    };

    setSearchKeyword(values.searchKeyword);
    setSearchWords(values.searchWords);
    setSearchStrtDt(values.searchStrtDt);
    setSearchEndDt(values.searchEndDt);

    dispatch(listTaxbills(searchData));
  };

  const apiRef = useGridApiRef();

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },

      {
        field: 'invNo',
        headerName: '계산서번호',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
        renderCell: (params) => (
          <Link
            underline="none"
            component={RouterLink}
            to={`/paymentmng/${params.row.invUuid}`}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'billTyp',
        headerName: '유형',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'reqPlanUsrHq',
        headerName: '본부',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'reqPlanUsrDpt',
        headerName: '부서',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'reqPlanUsrNm',
        headerName: '요청자',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'procCompDt',
        headerName: '수금예정일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        // valueGetter 삭제(2026-10-21): MUI X v7 시그니처는 (value, row, ...) 라서 value 자체가
        // 날짜 문자열인데, 예전 v5/v6 스타일로 value.value 를 읽고 있어 항상 undefined -> 늘 null로
        // 찍혀 날짜가 안 보였다(크래시는 안 났지만 빈칸). ResponsiveDataGrid 공통 보정(날짜 칸인데
        // valueGetter 없으면 자동으로 안전 변환)에 맡긴다.
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'procCompYn',
        headerName: '수금/지급 완료',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'procCompUsrNm',
        headerName: '처리자',
        description: '처리자',
        type: 'string',
        flex: 0.8,
        minWidth: 100,
      },
    ],
    [],
  );

  //검색 관련 사항
  const initialValues = {
    searchStrtDt: searchStrtDt,
    searchEndDt: searchEndDt,
    searchKeyword: searchKeyword,
    searchWords: searchWords,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '등록 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('등록 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        searchList(values);
        setSubmitting(false);
      }, 1000);
    },
  });

  useEffect(() => {
    searchList(formik.values);
  }, [searchBillTyp, searchTyp, searchYn]);

  const onChangeBillTyp = (billTyp) => {
    if (billTyp === searchBillTyp) {
      billTyp = '';
    }

    setsearchBillTyp(billTyp);
  };

  const onChangeYn = (useYn) => {
    if (useYn === searchBillTyp) {
      useYn = '';
    }

    setSearchYn(useYn);
  };

  const toggleSearchTyp = () => {
    setSearchTyp(!searchTyp);
  };

  // 검색 카드 "초기화" - 이 화면은 redux 필터/reset 액션이 없고 로컬 state라, 초기값으로
  // 직접 되돌리고 다시 조회한다(2026-10-22, taxIssuedmng과 동일한 패턴).
  const handleReset = () => {
    setsearchBillTyp('');
    setSearchTyp(false);
    setSearchYn('Y');
    setSearchKeyword('invNo');
    setSearchWords('');
    setSearchStrtDt('');
    setSearchEndDt('');
    dispatch(
      listTaxbills({
        billTyp: '',
        searchTyp: false,
        reqCompYn: 'Y',
        strtDt: '',
        endDt: '',
        invNo: '',
      }),
    );
  };

  return (
    <>
      <BreadcrumbsContainer
        title="세금계산서 관리/수금관리/계산서 리스트"
        icon="pay"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <TaxbillListBox formik={formik} onReset={handleReset} />
        <TaxbillStatusList
          searchBillTyp={searchBillTyp}
          onChangeBillTyp={onChangeBillTyp}
          searchYn={searchYn}
          onChangeYn={onChangeYn}
          searchTyp={searchTyp}
          toggleSearchTyp={toggleSearchTyp}
        />
        <TaxbillList
          columns={columns}
          rows={taxbills}
          error={error}
          loading={loading}
        />
      </Box>
    </>
  );
};

export default PaymentListContainer;
